import React, { useEffect, useMemo, useState } from 'react';
import { Save, Check, AlertTriangle } from 'lucide-react';
import trainingApi from '../api';
import { labelFor, nativeLabelFor } from '../i18n/strings';

/**
 * Translate one language's text for a whole training.
 *
 * The base-language text sits above every input as read-only reference, so whoever is
 * translating never has to switch tabs to see what they are translating. Leaving a
 * field empty is meaningful: it deletes the translation and the trainee sees the
 * base-language text instead of a blank.
 */
const TranslationsEditor = ({ trainingId, language, baseLanguage, course, translations, questions, onSaved }) => {
  const existing = translations?.[language] || {};

  const [title, setTitle] = useState(existing.title || '');
  const [description, setDescription] = useState(existing.description || '');
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  // Reset the form whenever the language tab changes or the training reloads.
  useEffect(() => {
    setTitle(existing.title || '');
    setDescription(existing.description || '');
    const next = {};
    for (const q of questions) {
      next[q.id] = {
        question_text: q.translations?.[language] || '',
        options: Object.fromEntries(
          q.options.map((o) => [o.id, o.translations?.[language] || ''])
        ),
      };
    }
    setDraft(next);
    setError('');
    setOkMsg('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, trainingId, questions]);

  const progress = useMemo(() => {
    let translated = 0;
    let optionsTranslated = 0;
    let optionsTotal = 0;
    for (const q of questions) {
      if ((draft[q.id]?.question_text || '').trim()) translated += 1;
      for (const o of q.options) {
        optionsTotal += 1;
        if ((draft[q.id]?.options?.[o.id] || '').trim()) optionsTranslated += 1;
      }
    }
    return {
      questions: translated,
      questionsTotal: questions.length,
      options: optionsTranslated,
      optionsTotal,
      complete: translated === questions.length && optionsTranslated === optionsTotal,
    };
  }, [draft, questions]);

  const setQuestionText = (qid, value) => {
    setDraft((cur) => ({ ...cur, [qid]: { ...cur[qid], question_text: value } }));
  };
  const setOptionText = (qid, oid, value) => {
    setDraft((cur) => ({
      ...cur,
      [qid]: { ...cur[qid], options: { ...cur[qid]?.options, [oid]: value } },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      await trainingApi.adminSaveTranslations(trainingId, {
        language,
        title,
        description,
        questions: questions.map((q) => ({
          question_id: q.id,
          question_text: draft[q.id]?.question_text ?? '',
          options: q.options.map((o) => ({
            option_id: o.id,
            option_text: draft[q.id]?.options?.[o.id] ?? '',
          })),
        })),
      });
      setOkMsg('Translations saved.');
      setTimeout(() => setOkMsg(''), 2500);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-semibold">
            {labelFor(language)} text
            <span className="text-slate-400 font-normal"> · {nativeLabelFor(language)}</span>
          </h3>
          <span className={`text-xs px-2 py-1 rounded border ${
            progress.complete
              ? 'border-emerald-700 bg-emerald-900/30 text-emerald-300'
              : 'border-amber-700 bg-amber-900/30 text-amber-300'
          }`}>
            {progress.complete ? (
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> Complete</span>
            ) : (
              `${progress.questions}/${progress.questionsTotal} questions · ${progress.options}/${progress.optionsTotal} answers`
            )}
          </span>
        </div>

        <p className="text-xs text-slate-400 mb-4">
          Anything left blank falls back to the {labelFor(baseLanguage)} text, so a partial
          translation is safe to save.
        </p>

        <FieldWithReference label="Title" reference={course.title}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={course.title}
            className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
          />
        </FieldWithReference>

        <FieldWithReference label="Description" reference={course.description}>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={course.description || ''}
            className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
          />
        </FieldWithReference>
      </div>

      <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
        <h3 className="font-semibold mb-1">Quiz ({questions.length} questions)</h3>
        <p className="text-xs text-slate-400 mb-4">
          The correct answer is set once on the {labelFor(baseLanguage)} version and applies to
          every language — you are only translating wording here, so a translation can never
          disagree about which answer is right.
        </p>

        {questions.length === 0 && (
          <div className="text-sm text-slate-400">No questions yet. Add them on the base language tab first.</div>
        )}

        <ol className="space-y-5">
          {questions.map((q, i) => (
            <li key={q.id} className="border-t border-slate-700 pt-4 first:border-t-0 first:pt-0">
              <FieldWithReference label={`Question ${i + 1}`} reference={q.question_text}>
                <textarea
                  rows={2}
                  value={draft[q.id]?.question_text || ''}
                  onChange={(e) => setQuestionText(q.id, e.target.value)}
                  className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
                />
              </FieldWithReference>

              <div className="pl-4 mt-2 space-y-2">
                {q.options.map((o) => (
                  <div key={o.id}>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      {o.is_correct ? (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-800">
                          correct
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 border border-slate-600">
                          option
                        </span>
                      )}
                      <span className="text-slate-500 truncate">{o.option_text}</span>
                    </div>
                    <input
                      type="text"
                      value={draft[q.id]?.options?.[o.id] || ''}
                      onChange={(e) => setOptionText(q.id, o.id, e.target.value)}
                      placeholder={o.option_text}
                      className="w-full px-3 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 text-sm"
                    />
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {error && (
        <div className="p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
      {okMsg && (
        <div className="p-3 rounded bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-sm">
          {okMsg}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2 disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : `Save ${labelFor(language)} text`}
        </button>
      </div>
    </div>
  );
};

const FieldWithReference = ({ label, reference, children }) => (
  <div className="mb-3">
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    {reference ? (
      <div className="text-sm text-slate-300 bg-slate-900/60 border border-slate-700/60 rounded px-3 py-1.5 mb-1.5">
        {reference}
      </div>
    ) : null}
    {children}
  </div>
);

export default TranslationsEditor;
